import React from "react";
import { kontentImage } from "../../types";
import {
  GatsbySeo,
  JsonLd,
  SitelinksSearchBoxJsonLd,
} from "gatsby-plugin-next-seo";
import { WebPage } from "gatsby-plugin-next-seo/node_modules/schema-dts";
import { stripHtml } from "../../lib/descriptions";

export interface SeoProps {
  pageTitle: string;
  pageUrl: string;
  pageDescription?: string;
  canonicalUrl?: string;
  robotsNoIndex?: boolean;
  robotsNoFollow?: boolean;
  sitemapInclude?: boolean;
  socialShareImage?: kontentImage;
  socialShareTitle?: string;
  socialShareDescription?: string;
  twitterCardType?: "summary" | "summary_large_image";
  lastModified: string;
  publishDate?: string;
  breadcrumbs: { title: string; url: string }[];
}

const Seo: React.FC<SeoProps> = ({
  pageTitle,
  pageUrl,
  pageDescription,
  publishDate,
  canonicalUrl,
  robotsNoIndex,
  robotsNoFollow,
  sitemapInclude,
  socialShareImage,
  socialShareTitle,
  socialShareDescription,
  lastModified,
  twitterCardType = "summary",
  breadcrumbs,
}) => {
  return (
    <>
      <GatsbySeo
        title={pageTitle}
        noindex={robotsNoIndex}
        language={"en-US"}
        nofollow={robotsNoFollow}
        description={pageDescription}
        canonical={canonicalUrl || pageUrl}
        twitter={{
          cardType: twitterCardType,
        }}
        openGraph={{
          url: pageUrl, // or page slug
          title: socialShareTitle,
          description: socialShareDescription,
          images: socialShareImage?.elements.image && [
            {
              url: socialShareImage.elements.image.value[0].url,
              width: socialShareImage.elements.image.value[0].width,
              height: socialShareImage.elements.image.value[0].height,
              alt: stripHtml(
                socialShareImage.elements.image.value[0]?.description || ""
              ),
            },
          ],
          article: {
            publishedTime: publishDate,
            modifiedTime: lastModified,
          },
        }}
      />
      <JsonLd<WebPage>
        json={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          url: pageUrl,
          name: pageTitle + " - Emprise Bank",
          dateModified: lastModified,
          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            "@id": "https://www.emprisebank.com/#website",
            url: "https://www.emprisebank.com",
            name: "Emprise Bank",
            description: "Banking",
            inLanguage: "en-US",
            publisher: {
              "@type": "Organization",
              "@id": "https://www.emprisebank.com/#organization",
              url: "https://www.emprisebank.com",
              sameAs: [
                "https://www.facebook.com/Emprise.Bank",
                "https://twitter.com/EmpriseBank",
              ],
              logo: {
                "@type": "ImageObject",
                "@id": "https://www.emprisebank.com/#logo",
                inLanguage: "en-US",
                url: "https://www.emprisebank.com/images/logo.svg",
                contentUrl: "https://www.emprisebank.com/images/logo.svg",
                caption: "Emprise Bank",
              },
              image: {
                "@type": "ImageObject",
                "@id": "https://www.emprisebank.com/#logo",
                inLanguage: "en-US",
                url: "https://www.emprisebank.com/images/logo.svg",
                contentUrl: "https://www.emprisebank.com/images/logo.svg",
                caption: "Emprise Bank",
              },
            },
          },
          about: {
            "@type": "WebSite",
            "@id": "https://www.emprisebank.com/#website",
            url: "https://www.emprisebank.com",
            name: "Emprise Bank",
            description: "Science, Engineering, Technology",
            inLanguage: "en-US",
            publisher: {
              "@type": "Organization",
              "@id": "https://www.emprisebank.com/#organization",
              url: "https://www.emprisebank.com",
              sameAs: [
                "https://www.facebook.com/Emprise.Bank",
                "https://twitter.com/EmpriseBank",
              ],
              logo: {
                "@type": "ImageObject",
                "@id": "https://www.emprisebank.com/#logo",
                inLanguage: "en-US",
                url: "https://www.emprisebank.com/images/logo.svg",
                contentUrl: "https://www.emprisebank.com/images/logo.svg",
                caption: "Emprise Bank",
              },
              image: {
                "@type": "ImageObject",
                "@id": "https://www.emprisebank.com/#logo",
                inLanguage: "en-US",
                url: "https://www.emprisebank.com/images/logo.svg",
                contentUrl: "https://www.emprisebank.com/images/logo.svg",
                caption: "Emprise Bank",
              },
            },
          },
          breadcrumb:
            breadcrumbs.length > 0
              ? {
                  "@type": "BreadcrumbList",
                  "@id": pageUrl + "/#breadcrumb",
                  name: "Breadcrumbs",
                  itemListElement: breadcrumbs.map((crumb, idx) => {
                    return {
                      "@type": "ListItem",
                      position: idx + 1,
                      item: {
                        "@id": "https://www.emprisebank.com" + crumb?.url,
                        name: crumb?.title,
                      },
                    };
                  }),
                }
              : undefined,
        }}
      />
      <SitelinksSearchBoxJsonLd
        url={"https://www.emprisebank.com/"}
        searchHandlerQueryStringUrl={"https://www.emprisebank.com/?q="}
      />
    </>
  );
};

export default Seo;
